import React, { useState } from 'react'
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import Add from '@mui/icons-material/Add'
import History from '@mui/icons-material/History'
import Email from '@mui/icons-material/Email'
import Phone from '@mui/icons-material/Phone'
import Message from '@mui/icons-material/Message'
import Notifications from '@mui/icons-material/Notifications'
import Assignment from '@mui/icons-material/Assignment'
import Person from '@mui/icons-material/Person'
import Schedule from '@mui/icons-material/Schedule'
import Description from '@mui/icons-material/Description'
import { styled } from '@mui/material/styles'
import { useFollowups } from '../hooks/useFollowups'
import { useCommunicationLogs } from '../hooks/useCommunicationLogs'
import FollowupChecklist from './FollowupChecklist'
import PrescriptionViewerWithFallback from './PrescriptionViewerWithFallback'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

dayjs.extend(utc)

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
  flexShrink: 0, // Prevent header from shrinking
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
  },
}))

const DrawerContent = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  minHeight: 0, // Important for flex children to allow shrinking
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.3)',
    },
  },
}))

const TimelineItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
}))

const getChannelIcon = (channel) => {
  switch (channel) {
    case 'mail':
      return <Email fontSize="small" />
    case 'whatsapp':
      return <Message fontSize="small" />
    case 'sms':
      return <Message fontSize="small" />
    case 'outcall':
      return <Phone fontSize="small" />
    case 'push-notification':
      return <Notifications fontSize="small" />
    default:
      return <Email fontSize="small" />
  }
}

const getChannelColor = (channel) => {
  switch (channel) {
    case 'mail':
      return 'primary'
    case 'whatsapp':
      return 'success'
    case 'sms':
      return 'info'
    case 'outcall':
      return 'warning'
    case 'push-notification':
      return 'secondary'
    default:
      return 'default'
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    let date = dayjs.utc(dateString, MOMENT_FORMATS.formatThatServerSends, true)
    if (!date.isValid()) {
      date = dayjs.utc(dateString)
    }
    if (!date.isValid()) {
      date = dayjs(dateString)
    }
    if (!date.isValid()) {
      return dateString
    }
    return date.local().format('DD MMM YYYY, HH:mm')
  } catch {
    return dateString
  }
}

const getRelativeTime = (dateString) => {
  if (!dateString) return null
  try {
    let date = dayjs.utc(dateString, MOMENT_FORMATS.formatThatServerSends, true)
    if (!date.isValid()) {
      date = dayjs.utc(dateString)
    }
    if (!date.isValid()) {
      date = dayjs(dateString)
    }
    if (!date.isValid()) {
      return null
    }
    
    const localDate = date.local()
    const now = dayjs()
    const diffMinutes = localDate.diff(now, 'minute')
    const absDiffMinutes = Math.abs(diffMinutes)
    
    if (absDiffMinutes < 1) {
      return 'Just now'
    } else if (absDiffMinutes < 60) {
      return `${absDiffMinutes} min${absDiffMinutes !== 1 ? 's' : ''} ago`
    } else if (absDiffMinutes < 1440) {
      const hours = Math.floor(absDiffMinutes / 60)
      const mins = absDiffMinutes % 60
      if (mins === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`
      }
      return `${hours}h ${mins}m ago`
    } else {
      const days = Math.floor(absDiffMinutes / 1440)
      if (days === 1) {
        return 'Yesterday'
      } else if (days < 7) {
        return `${days} days ago`
      } else {
        const weeks = Math.floor(days / 7)
        if (weeks < 4) {
          return `${weeks} week${weeks !== 1 ? 's' : ''} ago`
        } else {
          const months = Math.floor(days / 30)
          return `${months} month${months !== 1 ? 's' : ''} ago`
        }
      }
    }
  } catch {
    return null
  }
}

const getTimeElapsed = (dateString1, dateString2) => {
  if (!dateString1 || !dateString2) return null
  try {
    let date1 = dayjs.utc(dateString1, MOMENT_FORMATS.formatThatServerSends, true)
    if (!date1.isValid()) {
      date1 = dayjs.utc(dateString1)
    }
    if (!date1.isValid()) {
      date1 = dayjs(dateString1)
    }
    
    let date2 = dayjs.utc(dateString2, MOMENT_FORMATS.formatThatServerSends, true)
    if (!date2.isValid()) {
      date2 = dayjs.utc(dateString2)
    }
    if (!date2.isValid()) {
      date2 = dayjs(dateString2)
    }
    
    if (!date1.isValid() || !date2.isValid()) {
      return null
    }
    
    const localDate1 = date1.local()
    const localDate2 = date2.local()
    const diffMinutes = localDate1.diff(localDate2, 'minute')
    const absDiffMinutes = Math.abs(diffMinutes)
    
    if (absDiffMinutes < 1) {
      return null // Too small to show
    } else if (absDiffMinutes < 60) {
      return `${absDiffMinutes} min${absDiffMinutes !== 1 ? 's' : ''}`
    } else if (absDiffMinutes < 1440) {
      const hours = Math.floor(absDiffMinutes / 60)
      const mins = absDiffMinutes % 60
      if (mins === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`
      }
      return `${hours}h ${mins}m`
    } else {
      const days = Math.floor(absDiffMinutes / 1440)
      if (days === 1) {
        return '1 day'
      } else if (days < 7) {
        return `${days} days`
      } else {
        const weeks = Math.floor(days / 7)
        if (weeks < 4) {
          return `${weeks} week${weeks !== 1 ? 's' : ''}`
        } else {
          const months = Math.floor(days / 30)
          if (months < 12) {
            return `${months} month${months !== 1 ? 's' : ''}`
          } else {
            const years = Math.floor(months / 12)
            return `${years} year${years !== 1 ? 's' : ''}`
          }
        }
      }
    }
  } catch {
    return null
  }
}

const extractStepKey = (note) => {
  if (!note) return null
  const match = note.match(/^STEP:(\w+)\s*-/)
  return match ? match[1] : null
}

const getStepKeyLabel = (stepKey) => {
  const stepLabels = {
    TRIAGE: 'Triage',
    ASSIGN: 'Assign Therapist',
    CONTACT: 'Contact Patient',
    SCHEDULE: 'Schedule Session',
    PAYMENT: 'Payment',
    REMINDER: 'Reminder',
    CLOSE: 'Close Loop',
  }
  return stepLabels[stepKey] || stepKey
}

const getStepKeyColor = (stepKey) => {
  const stepColors = {
    TRIAGE: 'info',
    ASSIGN: 'primary',
    CONTACT: 'warning',
    SCHEDULE: 'secondary',
    PAYMENT: 'success',
    REMINDER: 'info',
    CLOSE: 'default',
  }
  return stepColors[stepKey] || 'default'
}

export default function FollowupsDrawer({ open, onClose, intervention }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [activeTab, setActiveTab] = useState(0)
  const [addFollowupOpen, setAddFollowupOpen] = useState(false)
  const [newFollowup, setNewFollowup] = useState({
    note: '',
    followupMode: 'manual',
    status: 'done',
    followupAt: null,
    nextFollowupAt: null,
  })

  // Determine action track (therapy or assessment)
  const hasTherapy = intervention?.therapy?.prescribedTherapy?.length > 0 ||
    intervention?.therapy?.therapiesNeeded?.length > 0 ||
    intervention?.therapy?.prescribedActions?.length > 0 ||
    intervention?.therapy?.suggestedBookingTherapy

  const hasAssessment = intervention?.assessment?.prescribedAssessments?.length > 0 ||
    intervention?.assessment?.psychAssessments?.length > 0

  const actionTrack = hasTherapy ? 'therapy' : hasAssessment ? 'assessment' : 'therapy'

  const {
    followups,
    loading: followupsLoading,
    error: followupsError,
    addFollowup,
    refreshFollowups,
  } = useFollowups(intervention?.bookedEventId, actionTrack, open && (activeTab === 1 || activeTab === 3))

  const {
    logs,
    loading: logsLoading,
    error: logsError,
  } = useCommunicationLogs(intervention?.patient?.patientId, intervention?.bookedEventId, open && (activeTab === 2 || activeTab === 3))

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleAddFollowup = async () => {
    if (!newFollowup.note.trim()) {
      return
    }

    const result = await addFollowup({
      ...newFollowup,
      followupAt: newFollowup.followupAt ? newFollowup.followupAt.toISOString() : undefined,
      nextFollowupAt: newFollowup.nextFollowupAt ? newFollowup.nextFollowupAt.toISOString() : undefined,
    })

    if (result.success) {
      setAddFollowupOpen(false)
      setNewFollowup({
        note: '',
        followupMode: 'manual',
        status: 'done',
        followupAt: null,
        nextFollowupAt: null,
      })
    }
  }

  // Combine and sort timeline items
  const timelineItems = React.useMemo(() => {
    const items = []

    // Add initial "Intervention Requested" entry (first entry in timeline)
    if (intervention) {
      const hasTherapy = intervention?.therapy?.prescribedTherapy?.length > 0 ||
        intervention?.therapy?.therapiesNeeded?.length > 0 ||
        intervention?.therapy?.prescribedActions?.length > 0 ||
        intervention?.therapy?.suggestedBookingTherapy

      const hasAssessment = intervention?.assessment?.prescribedAssessments?.length > 0 ||
        intervention?.assessment?.psychAssessments?.length > 0

      let interventionType = ''
      if (hasTherapy && hasAssessment) {
        interventionType = 'Therapy & Assessment'
      } else if (hasTherapy) {
        interventionType = 'Therapy'
      } else if (hasAssessment) {
        interventionType = 'Assessment'
      } else {
        interventionType = 'Intervention'
      }

      // Use eventStartTime or fallback to current time if not available
      const requestTimestamp = intervention?.eventStartTime || intervention?.prescriptionCreatedAt || new Date().toISOString()
      
      items.push({
        type: 'intervention-request',
        id: `intervention-${intervention?.bookedEventId || intervention?.prescriptionId || 'initial'}`,
        timestamp: requestTimestamp,
        interventionType,
        requestedBy: intervention?.mentor?.name || 'Doctor',
        eventType: intervention?.eventType || 'Consultation',
        eventMode: intervention?.eventMode || 'N/A',
        clinicLocation: intervention?.clinicLocationSlug || intervention?.clinicBrandSlug || 'N/A',
      })
    }

    // Add followups
    followups.forEach((followup) => {
      items.push({
        type: 'followup',
        id: followup.id,
        timestamp: followup.followupAt,
        note: followup.note,
        performedByName: followup.performedByName,
        status: followup.status,
        followupMode: followup.followupMode,
        nextFollowupAt: followup.nextFollowupAt,
        stepKey: extractStepKey(followup.note) || followup.stepKey,
      })
    })

    // Add communication logs
    logs.forEach((log) => {
      items.push({
        type: 'communication',
        id: log.id,
        timestamp: log.sentAt,
        channel: log.channel,
        templateTitle: log.templateTitle,
        status: log.status,
        recipient: log.recipient,
      })
    })

    // Sort by timestamp descending (most recent first)
    return items.sort((a, b) => {
      const timeA = dayjs.utc(a.timestamp, MOMENT_FORMATS.formatThatServerSends).valueOf()
      const timeB = dayjs.utc(b.timestamp, MOMENT_FORMATS.formatThatServerSends).valueOf()
      return timeB - timeA
    })
  }, [followups, logs, intervention])

  if (!intervention) {
    return null
  }

  return (
    <>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 900,
            maxWidth: isMobile ? '100%' : '90vw',
            maxHeight: isMobile ? '90vh' : '100vh',
            height: isMobile ? '90vh' : '100vh',
            borderTopLeftRadius: isMobile ? theme.shape.borderRadius * 2 : 0,
            borderTopRightRadius: isMobile ? theme.shape.borderRadius * 2 : 0,
            boxShadow: isMobile
              ? '0 -4px 20px rgba(0, 0, 0, 0.15)'
              : '0 4px 20px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        ModalProps={{
          keepMounted: false,
          disableScrollLock: false,
        }}
      >
        <DrawerHeader>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Followups & Communications
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {intervention?.patient?.name} - {intervention?.mentor?.name}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="followups tabs">
            <Tab
              icon={<Assignment />}
              iconPosition="start"
              label="Checklist"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<History />}
              iconPosition="start"
              label="Followups"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<Email />}
              iconPosition="start"
              label="Communications"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<History />}
              iconPosition="start"
              label="Timeline"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<Description />}
              iconPosition="start"
              label="Referring Prescription"
              sx={{ minHeight: 64 }}
            />
          </Tabs>
        </Box>

        <DrawerContent sx={{ p: 3 }}>
          {/* Checklist Tab */}
          {activeTab === 0 && (
            <FollowupChecklist
              intervention={intervention}
              onRefresh={() => {
                refreshFollowups()
              }}
            />
          )}

          {/* Followups Tab */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Followup Entries
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setAddFollowupOpen(true)}
                  size="small"
                >
                  Add Followup
                </Button>
              </Box>

              {followupsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : followupsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {followupsError}
                </Alert>
              ) : followups.length === 0 ? (
                <Alert severity="info">
                  No followups recorded yet. Click "Add Followup" to create one.
                </Alert>
              ) : (
                <Box sx={{ position: 'relative', pl: 4 }}>
                  {/* Vertical Timeline Line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      backgroundColor: 'divider',
                    }}
                  />
                  
                  <Stack spacing={3}>
                    {followups.map((followup, index) => {
                      const stepKey = extractStepKey(followup.note) || followup.stepKey
                      const noteWithoutStep = followup.note?.replace(/^STEP:\w+\s*-\s*/, '') || followup.note
                      const relativeTime = getRelativeTime(followup.followupAt)
                      const isLatest = index === 0
                      
                      return (
                        <Box
                          key={followup.id}
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            gap: 2,
                          }}
                        >
                          {/* Timeline Dot */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: -32,
                              top: 8,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: isLatest ? 'primary.main' : 'divider',
                              border: `3px solid`,
                              borderColor: 'background.paper',
                              zIndex: 1,
                              boxShadow: isLatest ? '0 0 0 4px rgba(67, 56, 202, 0.1)' : 'none',
                            }}
                          />
                          
                          {/* Content Card */}
                          <Paper
                            elevation={isLatest ? 4 : 1}
                            sx={{
                              flex: 1,
                              p: 2.5,
                              borderRadius: 2,
                              border: isLatest ? `2px solid` : '1px solid',
                              borderColor: isLatest ? 'primary.main' : 'divider',
                              backgroundColor: isLatest ? 'action.selected' : 'background.paper',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                boxShadow: 3,
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <Stack spacing={1.5}>
                              {/* Header with Step Key and Time */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  {stepKey && (
                                    <Chip
                                      label={getStepKeyLabel(stepKey)}
                                      color={getStepKeyColor(stepKey)}
                                      size="small"
                                      sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        fontSize: '0.75rem',
                                        height: 24,
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="body1"
                                    fontWeight={isLatest ? 600 : 500}
                                    sx={{
                                      color: isLatest ? 'primary.main' : 'text.primary',
                                      mb: 0.5,
                                    }}
                                  >
                                    {noteWithoutStep}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.5 }}
                                  >
                                    {formatDateTime(followup.followupAt)}
                                  </Typography>
                                  {relativeTime && (
                                    <Chip
                                      label={relativeTime}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.7rem',
                                        height: 20,
                                        backgroundColor: 'action.hover',
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              
                              {/* Metadata Row */}
                              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                <Chip
                                  label={followup.status}
                                  size="small"
                                  color={followup.status === 'done' ? 'success' : followup.status === 'pending' ? 'warning' : 'default'}
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                                <Chip
                                  label={followup.followupMode}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                                {followup.performedByName && (
                                  <Chip
                                    icon={<Person />}
                                    label={followup.performedByName}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                  />
                                )}
                              </Stack>
                              
                              {/* Next Followup */}
                              {followup.nextFollowupAt && (
                                <Alert
                                  severity="info"
                                  icon={<Schedule />}
                                  sx={{
                                    py: 0.5,
                                    '& .MuiAlert-message': {
                                      fontSize: '0.75rem',
                                    },
                                  }}
                                >
                                  Next followup: {formatDateTime(followup.nextFollowupAt)}
                                  {getRelativeTime(followup.nextFollowupAt) && (
                                    <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                                      ({getRelativeTime(followup.nextFollowupAt)})
                                    </Typography>
                                  )}
                                </Alert>
                              )}
                            </Stack>
                          </Paper>
                        </Box>
                      )
                    })}
                  </Stack>
                </Box>
              )}
            </Box>
          )}

          {/* Communications Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Communication Logs
              </Typography>

              {logsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : logsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {logsError}
                </Alert>
              ) : logs.length === 0 ? (
                <Alert severity="info">No communication logs found.</Alert>
              ) : (
                <Stack spacing={2}>
                  {logs.map((log) => (
                    <TimelineItem key={log.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            {getChannelIcon(log.channel)}
                            <Typography variant="body1" fontWeight={500}>
                              {log.templateTitle || 'Communication'}
                            </Typography>
                            <Chip
                              icon={getChannelIcon(log.channel)}
                              label={log.channel}
                              size="small"
                              color={getChannelColor(log.channel)}
                              variant="outlined"
                            />
                            <Chip
                              label={log.status}
                              size="small"
                              color={log.status === 'sent' ? 'success' : 'error'}
                            />
                          </Stack>
                          {log.recipient && (
                            <Typography variant="body2" color="text.secondary">
                              To: {log.recipient}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(log.sentAt)}
                        </Typography>
                      </Box>
                    </TimelineItem>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* Timeline Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Unified Timeline
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Complete chronological view of all interactions and followups for this intervention.
              </Typography>

              {(followupsLoading || logsLoading) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : timelineItems.length === 0 ? (
                <Alert severity="info">No timeline entries found.</Alert>
              ) : (
                <Box sx={{ position: 'relative', pl: { xs: 4, sm: 5 } }}>
                  {/* Vertical Timeline Line - Gradient from start to end */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: 12, sm: 16 },
                      top: 0,
                      bottom: 0,
                      width: 3,
                      background: 'linear-gradient(to bottom, rgba(67, 56, 202, 0.8) 0%, rgba(67, 56, 202, 0.4) 50%, rgba(0, 0, 0, 0.1) 100%)',
                      borderRadius: '2px',
                      zIndex: 0,
                    }}
                  />
                  
                  <Stack spacing={0}>
                    {timelineItems.map((item, index) => {
                      const relativeTime = getRelativeTime(item.timestamp)
                      const isLatest = index === 0
                      const isInterventionRequest = item.type === 'intervention-request'
                      const isFirst = index === timelineItems.length - 1
                      
                      // Calculate time elapsed between this item and the previous one
                      const timeElapsed = index > 0 
                        ? getTimeElapsed(timelineItems[index - 1].timestamp, item.timestamp)
                        : null
                      
                      // Determine colors based on type - improved contrast and legibility
                      let dotColor = 'divider'
                      let borderColor = 'divider'
                      let backgroundColor = 'background.paper'
                      let leftColumnBg = 'grey.100'
                      let textColor = 'text.primary'
                      
                      if (isInterventionRequest) {
                        dotColor = 'success.main'
                        borderColor = 'success.main'
                        backgroundColor = 'rgba(46, 125, 50, 0.08)' // success.light with better opacity
                        leftColumnBg = 'rgba(46, 125, 50, 0.12)'
                        textColor = 'success.dark'
                      } else if (item.type === 'followup') {
                        if (isLatest) {
                          dotColor = 'primary.main'
                          borderColor = 'primary.main'
                          backgroundColor = 'rgba(67, 56, 202, 0.08)' // primary.light with better opacity
                          leftColumnBg = 'rgba(67, 56, 202, 0.12)'
                          textColor = 'primary.dark'
                        } else {
                          dotColor = 'primary.light'
                          borderColor = 'primary.light'
                          backgroundColor = 'background.paper'
                          leftColumnBg = 'rgba(67, 56, 202, 0.04)'
                          textColor = 'text.primary'
                        }
                      } else if (item.type === 'communication') {
                        if (isLatest) {
                          dotColor = 'secondary.main'
                          borderColor = 'secondary.main'
                          backgroundColor = 'rgba(156, 39, 176, 0.08)' // secondary.light with better opacity
                          leftColumnBg = 'rgba(156, 39, 176, 0.12)'
                          textColor = 'secondary.dark'
                        } else {
                          dotColor = 'secondary.light'
                          borderColor = 'secondary.light'
                          backgroundColor = 'background.paper'
                          leftColumnBg = 'rgba(156, 39, 176, 0.04)'
                          textColor = 'text.primary'
                        }
                      }
                      
                      // Parse date and time
                      const dateTimeParts = formatDateTime(item.timestamp).split(',')
                      const datePart = dateTimeParts[0] || ''
                      const timePart = dateTimeParts[1]?.trim() || ''
                      
                      return (
                        <React.Fragment key={`${item.type}-${item.id}`}>
                          {/* Time Elapsed Between Cards */}
                          {timeElapsed && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 1.5,
                                position: 'relative',
                                mb: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  px: 2,
                                }}
                              >
                                {/* Horizontal Line */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: '50%',
                                    height: '1px',
                                    backgroundColor: 'divider',
                                    zIndex: 0,
                                  }}
                                />
                                {/* Time Elapsed Chip */}
                                <Chip
                                  label={timeElapsed}
                                  size="small"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 24,
                                    backgroundColor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    position: 'relative',
                                    zIndex: 1,
                                    mx: 'auto',
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          
                          {/* Timeline Card */}
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'stretch',
                              minHeight: 100,
                              mb: 3,
                            }}
                          >
                          {/* Timeline Dot */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: { xs: 6, sm: 10 },
                              top: 16,
                              width: { xs: 14, sm: 18 },
                              height: { xs: 14, sm: 18 },
                              borderRadius: '50%',
                              backgroundColor: dotColor,
                              border: `3px solid`,
                              borderColor: 'background.paper',
                              zIndex: 2,
                              boxShadow: isLatest || isFirst ? (() => {
                                const colorParts = dotColor.split('.')
                                if (colorParts.length === 2) {
                                  const colorName = colorParts[0]
                                  const shade = colorParts[1]
                                  const color = theme.palette[colorName]?.[shade]
                                  if (color) {
                                    return `0 0 0 4px ${color}20`
                                  }
                                }
                                return `0 0 0 4px rgba(0, 0, 0, 0.1)`
                              })() : '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                          />
                          
                          {/* Content Card with Two Columns */}
                          <Paper
                            elevation={isLatest ? 4 : 1}
                            sx={{
                              flex: 1,
                              display: 'flex',
                              borderRadius: 2,
                              border: `2px solid`,
                              borderColor: borderColor,
                              backgroundColor: backgroundColor,
                              transition: 'all 0.2s ease-in-out',
                              overflow: 'hidden',
                              '&:hover': {
                                boxShadow: 3,
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            {/* Left Column - Date/Time */}
                            <Box
                              sx={{
                                width: { xs: 90, sm: 110 },
                                minWidth: { xs: 90, sm: 110 },
                                backgroundColor: leftColumnBg,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: { xs: 1.5, sm: 2 },
                                borderRight: '2px solid',
                                borderColor: borderColor,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                  fontWeight: 700,
                                  color: isLatest ? textColor : 'text.secondary',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  mb: 0.5,
                                  textAlign: 'center',
                                }}
                              >
                                {datePart.split(' ')[1] || ''}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                  fontWeight: 700,
                                  color: isLatest ? textColor : 'text.primary',
                                  lineHeight: 1.2,
                                  mb: 0.5,
                                }}
                              >
                                {datePart.split(' ')[0] || ''}
                              </Typography>
                              {timePart && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    fontWeight: 600,
                                    color: isLatest ? textColor : 'text.secondary',
                                    mb: 1,
                                  }}
                                >
                                  {timePart}
                                </Typography>
                              )}
                              {relativeTime && (
                                <Chip
                                  label={relativeTime}
                                  size="small"
                                  sx={{
                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                    height: { xs: 18, sm: 20 },
                                    backgroundColor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: isLatest ? borderColor : 'divider',
                                    fontWeight: 500,
                                    maxWidth: '100%',
                                    color: isLatest ? textColor : 'text.secondary',
                                  }}
                                />
                              )}
                            </Box>
                            
                            {/* Right Column - Content */}
                            <Box
                              sx={{
                                flex: 1,
                                p: { xs: 2, sm: 2.5 },
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Stack spacing={1.5} sx={{ flex: 1 }}>
                                  {isInterventionRequest ? (
                                    <>
                                      <Chip
                                        label={item.interventionType}
                                        color="success"
                                        size="small"
                                        sx={{
                                          fontWeight: 600,
                                          mb: 1,
                                          fontSize: '0.75rem',
                                          height: 24,
                                        }}
                                      />
                                      <Typography
                                        variant="body1"
                                        fontWeight={600}
                                        sx={{
                                          color: 'success.dark',
                                          mb: 0.5,
                                        }}
                                      >
                                        Intervention Requested
                                      </Typography>
                                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                        Requested by <strong>{item.requestedBy}</strong>
                                      </Typography>
                                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                                        <Chip
                                          label={item.eventType}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                        <Chip
                                          label={item.eventMode}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                        <Chip
                                          label={item.clinicLocation}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                      </Stack>
                                    </>
                                  ) : item.type === 'followup' ? (
                                    <>
                                      {item.stepKey && (
                                        <Chip
                                          label={getStepKeyLabel(item.stepKey)}
                                          color={getStepKeyColor(item.stepKey)}
                                          size="small"
                                          sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            fontSize: '0.75rem',
                                            height: 24,
                                          }}
                                        />
                                      )}
                                      <Typography
                                        variant="body1"
                                        fontWeight={isLatest ? 600 : 500}
                                        sx={{
                                          color: isLatest ? 'primary.dark' : 'text.primary',
                                          mb: 0.5,
                                        }}
                                      >
                                        {item.note?.replace(/^STEP:\w+\s*-\s*/, '') || item.note}
                                      </Typography>
                                      {item.performedByName && (
                                        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                          By: <strong>{item.performedByName}</strong>
                                        </Typography>
                                      )}
                                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                                        <Chip
                                          label={item.status}
                                          size="small"
                                          color={item.status === 'done' ? 'success' : item.status === 'pending' ? 'warning' : 'default'}
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                        <Chip
                                          label={item.followupMode}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                      </Stack>
                                      {item.nextFollowupAt && (
                                        <Alert
                                          severity="info"
                                          icon={<Schedule />}
                                          sx={{
                                            mt: 1,
                                            py: 0.5,
                                            '& .MuiAlert-message': {
                                              fontSize: '0.75rem',
                                            },
                                          }}
                                        >
                                          Next followup: {formatDateTime(item.nextFollowupAt)}
                                          {getRelativeTime(item.nextFollowupAt) && (
                                            <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                                              ({getRelativeTime(item.nextFollowupAt)})
                                            </Typography>
                                          )}
                                        </Alert>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                        {getChannelIcon(item.channel)}
                                        <Typography variant="body1" fontWeight={isLatest ? 600 : 500}>
                                          {item.templateTitle || 'Communication'}
                                        </Typography>
                                      </Stack>
                                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                                        <Chip
                                          icon={getChannelIcon(item.channel)}
                                          label={item.channel}
                                          size="small"
                                          color={getChannelColor(item.channel)}
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                        <Chip
                                          label={item.status}
                                          size="small"
                                          color={item.status === 'sent' ? 'success' : 'error'}
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                      </Stack>
                                      {item.recipient && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                          To: {item.recipient}
                                        </Typography>
                                      )}
                                    </>
                                  )}
                              </Stack>
                            </Box>
                          </Paper>
                        </Box>
                      </React.Fragment>
                      )
                    })}
                  </Stack>
                </Box>
              )}
            </Box>
          )}

          {/* Referring Prescription Tab */}
          {activeTab === 4 && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <PrescriptionViewerWithFallback intervention={intervention} />
            </Box>
          )}
        </DrawerContent>
      </Drawer>

      {/* Add Followup Dialog */}
      <Dialog
        open={addFollowupOpen}
        onClose={() => setAddFollowupOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Followup Entry</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Note"
                multiline
                rows={4}
                fullWidth
                value={newFollowup.note}
                onChange={(e) => setNewFollowup({ ...newFollowup, note: e.target.value })}
                required
              />
              <FormControl fullWidth>
                <InputLabel>Followup Mode</InputLabel>
                <Select
                  value={newFollowup.followupMode}
                  onChange={(e) => setNewFollowup({ ...newFollowup, followupMode: e.target.value })}
                  label="Followup Mode"
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="automated">Automated</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newFollowup.status}
                  onChange={(e) => setNewFollowup({ ...newFollowup, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="done">Done</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <DatePicker
                label="Followup At (optional)"
                value={newFollowup.followupAt}
                onChange={(date) => setNewFollowup({ ...newFollowup, followupAt: date })}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DatePicker
                label="Next Followup At (optional)"
                value={newFollowup.nextFollowupAt}
                onChange={(date) => setNewFollowup({ ...newFollowup, nextFollowupAt: date })}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFollowupOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddFollowup}
            variant="contained"
            disabled={!newFollowup.note.trim()}
          >
            Add Followup
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

